import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  // Bell,
  // Calendar,
  Contact,
  // Eye,
  Flow,
  Folder,
  Home,
  // Inbox,
  // Outreach,
  Reports,
  Search,
  Settings,
  Templates,
  Circles,
  Interview,
  // Stack,
} from '@/assets/icons/nav-v2';
import { NewAppNotification } from '@/components/notifications';
import { getReviews, reviewsApiActions } from '@/store/reviews';
import { usePermissionGuard } from '@/hooks/usePermissionGuard';
import { NavLink, TaskNav, NavSearch, NavProfile, Divider } from './components'; // add NavMenu component for secondary menu
import { NewTaskButton } from './components/NewTaskButton';
import { useIsExternalUser } from '@/hooks/useIsExternalUser';
import { LayersThree02 } from '@kula-ai/icons/dual-tone/LayersThree02';
import { UsersRight } from '@kula-ai/icons/outline/UsersRight';
import { Rocket02 } from '@kula-ai/icons/outline/Rocket02';
import { MessageChatSquare } from '@kula-ai/icons/outline/MessageChatSquare';
import { BookOpen01 } from '@kula-ai/icons/outline/BookOpen01';
import { Zap } from '@kula-ai/icons/outline/Zap';
import { Signal02 } from '@kula-ai/icons/outline/Signal02';
import { AnnotationCheck } from '@kula-ai/icons/outline/AnnotationCheck';
import { usePermission } from '@/ui/permission';
import { useAtsPermissions } from '@/hooks/useAtsPermissions';
import { HelpSupport } from './help-and-support';
import { useKFlags } from '@/hooks/use-k-flags';
import { selectCurrUser } from '@/store/bootstrap/selectors';
import { useAppMode } from '@/store/app-mode';

export const useNavigationConfig = () => {
  const {
    feSettingsNav,
    feProjects,
    ats_enabled,
    feNoteTaker,
    feInterviewsRevamp,
    feReq,
    feAgentForce,
  } = useKFlags();
  const { isCirclesEnabled, isFlowEnabled, isReportsEnabled } =
    usePermissionGuard();
  const dispatch = useDispatch();
  const {
    navigationReviews: {
      data: { data: { metrics_count: { awaiting_my_review } = {} } = {} } = {},
    },
  } = useSelector(getReviews);

  const isContactsEnabled = usePermission({
    permission: 'circles.contacts?',
  });
  const {
    isJobsVisible,
    isReportsEnabledForAtsUsers,
    requisitionsPermissions,
  } = useAtsPermissions();

  const isExternalUser = useIsExternalUser();

  const isCommTemplatesEnabledForAts = usePermission({
    permission: 'api_ats_v1_ats_comm_template.create?',
  });

  const currUser = useSelector(selectCurrUser);

  const isRequisitionsEnabled =
    feReq && currUser?.requisition?.enabled && requisitionsPermissions.index;

  const isJobsOrRequisitionsActive =
    window.location.pathname.startsWith('/jobs') ||
    window.location.pathname.startsWith('/requisitions');

  useEffect(() => {
    // Use separate reducer key for navigation data to avoid race condition with InternalReviewsList
    dispatch(reviewsApiActions('fetchNavigationReviews'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const navItemsTopSection = [
    {
      component: NavSearch,
      props: { title: 'Search', icon: Search },
      styles: { my: '12px' },
      isEnabled: !ats_enabled,
    },
    {
      component: NewAppNotification,
      isEnabled: true,
      isAllowedToExternalUsers: true,
    },
    {
      component: TaskNav,
      isEnabled: isFlowEnabled || ats_enabled,
    },
    {
      component: Divider,
      props: {},
      styles: {
        borderBottom: '1px solid',
        borderBottomColor: 'secondary-100',
        my: '12px',
      },
      isEnabled: true,
      isAllowedToExternalUsers: true,
    },
  ];

  const navItemsMiddleSection = [
    {
      component: NavLink,
      props: {
        link: '/dashboard',
        title: 'Dashboard',
        icon: Home,
        testID: 'dashboard',
      },
      isEnabled: true, // all
    },
    {
      component: NavLink,
      props: {
        link: '/jobs',
        title: isRequisitionsEnabled ? 'Jobs & Requisitions' : 'Jobs',
        icon: LayersThree02,
        testID: 'jobs',
        // route is jobs or requisitions, so we need to show active state for both
        isCustomActive: isJobsOrRequisitionsActive,
      },
      // Todo-Req: Add permission check for requisitions later
      isEnabled: isJobsVisible && ats_enabled,
    },
    // {
    //   component: NavMenu,
    //   props: {
    //     // link: '/flows',
    //     title: 'Outreach',
    //     icon: Flow,
    //     menuItem: outReachItems,
    //   },
    //   isEnabled: isFlowEnabled,
    // },
    {
      component: NavLink,
      props: {
        link: '/flows',
        title: 'Flows',
        icon: Flow,
        testID: 'flows',
      },
      isEnabled: isFlowEnabled,
    },
    {
      component: NavLink,
      props: {
        link: '/projects',
        title: 'Projects',
        icon: Folder,
        count: awaiting_my_review,
        testID: 'projects',
      },
      isEnabled: (feProjects && isFlowEnabled) || isExternalUser,
      isAllowedToExternalUsers: true,
    },

    {
      component: NavLink,
      props: {
        link: '/circles',
        title: 'Circles',
        icon: Circles,
        testID: 'circles',
      },
      isEnabled: isCirclesEnabled,
    },
    {
      component: NavLink,
      props: {
        link: '/contacts',
        title: 'Contacts',
        icon: Contact,
        testID: 'contacts',
      },
      isEnabled: isFlowEnabled || isContactsEnabled,
    },
    {
      component: NavLink,
      props: {
        link: feInterviewsRevamp ? '/interviews/upcoming' : '/interviews/all',
        title: 'Interviews',
        icon: Interview,
        testID: 'interviews',
      },
      isEnabled: feNoteTaker || ats_enabled, // all can see
    },
    {
      component: NavLink,
      props: {
        link: '/templates',
        title: 'Templates',
        icon: Templates,
        testID: 'templates',
      },
      isEnabled: ats_enabled
        ? isFlowEnabled && isCommTemplatesEnabledForAts
        : isFlowEnabled,
    },
    {
      component: NavLink,
      props: {
        link: ats_enabled ? '/reports' : '/reports-home',
        title: 'Reports and Analytics',
        icon: Reports,
        testID: 'reports',
      },
      isEnabled:
        (isReportsEnabled && isFlowEnabled) || isReportsEnabledForAtsUsers,
    },
    {
      component: NavLink,
      props: {
        link: '/referrals',
        title: 'Referrals',
        icon: UsersRight,
        testID: 'referrals',
      },
      isEnabled: ats_enabled, // For all if ats enabled
    },
  ];

  const navBottomItems = [
    {
      component: Divider,
      styles: {
        borderBottom: '1px solid',
        borderBottomColor: 'secondary-100',
        my: '12px',
      },
      props: {},
      isEnabled: isFlowEnabled,
    },
    {
      component: HelpSupport,
      isEnabled: ats_enabled,
    },
    {
      component: NavLink,
      props: {
        link: ats_enabled
          ? '/settings'
          : feSettingsNav
            ? '/settings/profile/general'
            : '/settings/profile',
        title: 'Settings',
        icon: Settings,
        testID: 'settings',
      },
      isEnabled: true,
    },
    {
      component: NavProfile,
      isEnabled: !ats_enabled,
      isAllowedToExternalUsers: true,
    },
  ];

  // --- AgentForge mode navigation ---
  const { mode } = useAppMode();

  const agentforgeNavTopSection = [
    {
      component: NewTaskButton,
      isEnabled: true,
    },
    {
      component: Divider,
      props: {},
      styles: {
        borderBottom: '1px solid',
        borderBottomColor: 'secondary-100',
        my: '12px',
      },
      isEnabled: true,
    },
  ];

  const agentforgeNavMiddleSection = [
    {
      component: NavLink,
      props: {
        link: '/forge/dashboard',
        title: 'Dashboard',
        icon: Home,
        testID: 'agentforge-dashboard',
      },
      isEnabled: true,
    },
    {
      component: NavLink,
      props: {
        link: '/forge/agents',
        title: 'Agents',
        icon: Rocket02,
        testID: 'agentforge-agents',
      },
      isEnabled: true,
    },
    {
      component: NavLink,
      props: {
        link: '/forge/chat',
        title: 'Chat',
        icon: MessageChatSquare,
        testID: 'agentforge-chat',
      },
      isEnabled: true,
    },
    {
      component: NavLink,
      props: {
        link: '/forge/knowledge',
        title: 'Knowledge',
        icon: BookOpen01,
        testID: 'agentforge-knowledge',
      },
      isEnabled: true,
    },
    {
      component: NavLink,
      props: {
        link: '/forge/skills',
        title: 'Skills',
        icon: Zap,
        testID: 'agentforge-skills',
      },
      isEnabled: true,
    },
    {
      component: NavLink,
      props: {
        link: '/forge/channels',
        title: 'Channels',
        icon: Signal02,
        testID: 'agentforge-channels',
      },
      isEnabled: true,
    },
    {
      component: NavLink,
      props: {
        link: '/forge/feedback',
        title: 'Feedback',
        icon: AnnotationCheck,
        testID: 'agentforge-feedback',
      },
      isEnabled: true,
    },
  ];

  const agentforgeNavBottomItems = [
    {
      component: Divider,
      styles: {
        borderBottom: '1px solid',
        borderBottomColor: 'secondary-100',
        my: '12px',
      },
      props: {},
      isEnabled: true,
    },
    {
      component: NavLink,
      props: {
        link: ats_enabled
          ? '/settings'
          : feSettingsNav
            ? '/settings/profile/general'
            : '/settings/profile',
        title: 'Settings',
        icon: Settings,
        testID: 'settings',
      },
      isEnabled: true,
    },
    {
      component: NavProfile,
      isEnabled: !ats_enabled,
      isAllowedToExternalUsers: true,
    },
  ];

  const isAgentForgeMode = feAgentForce && mode === 'agentforge';

  return {
    navItemsTopSection: isAgentForgeMode
      ? agentforgeNavTopSection
      : navItemsTopSection,
    navItemsMiddleSection: isAgentForgeMode
      ? agentforgeNavMiddleSection
      : navItemsMiddleSection,
    navBottomItems: isAgentForgeMode
      ? agentforgeNavBottomItems
      : navBottomItems,
  };
};
